import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { ContestsManager } from '@/components/Contests';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, BookOpen, LogOut, Plus, Trash2, CheckCircle, XCircle,
  CalendarDays, ClipboardList, StickyNote, Video, User, Eye,
  Clock, BarChart3, GraduationCap, MonitorPlay, Loader2,
  ChevronDown, ChevronUp, MessageSquare, Target, Trophy, Crown, Zap,
  Archive, FolderOpen, FileSpreadsheet, Save, Paperclip, Download, File as FileIcon, X,
  AlertTriangle, Mail, MailOpen, Star, Award, Camera, Phone, Edit3,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'students', label: 'طلابي', icon: Users },
  { id: 'weak-students', label: 'يحتاجون متابعة', icon: AlertTriangle },
  { id: 'grades', label: 'كشف العلامات', icon: FileSpreadsheet },
  { id: 'weekly-plan', label: 'الخطة الأسبوعية', icon: CalendarDays },
  { id: 'assignments', label: 'الواجبات والمهام', icon: ClipboardList },
  { id: 'messages', label: 'رسائل المدير', icon: Mail },
  { id: 'minister-msgs', label: 'رسائل الوزير', icon: Star },
  { id: 'videos', label: 'فيديوهاتي', icon: Video },
  { id: 'contests', label: 'المسابقات', icon: Award },
];

const DAYS = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس'];
const PERIODS = ['الحصة 1', 'الحصة 2', 'الحصة 3', 'الحصة 4', 'الحصة 5', 'الحصة 6'];

export default function TeacherPage() {
  const { user, logout, getAuthHeaders, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('students');
  const [mySubjects, setMySubjects] = useState([]);
  const [showProfile, setShowProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: '', bio: '', email: '', phone: '' });
  const [displayName, setDisplayName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMeta, setProfileMeta] = useState({ bio: '', email: '', phone: '' });
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [counts, setCounts] = useState({ students: 0, exams: 0, assignments: 0, videos: 0 });

  useEffect(() => {
    if (user && user.role !== 'teacher') navigate('/grades');
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === 'teacher') {
      const h = getAuthHeaders();
      axios.get(`${API_URL}/api/teacher/my-subjects`, { headers: h })
        .then(r => setMySubjects(r.data)).catch(() => {});
      axios.get(`${API_URL}/api/auth/me`, { headers: h })
        .then(async r => {
          setProfileForm({ name: r.data.name || '', bio: r.data.bio || '', email: r.data.email || '', phone: r.data.phone || '' });
          setProfileMeta({ bio: r.data.bio || '', email: r.data.email || '', phone: r.data.phone || '' });
          setDisplayName(r.data.name || '');
          if (r.data.avatar_path) {
            try {
              const av = await axios.get(`${API_URL}/api/profile/avatar/${r.data.avatar_path}`, { headers: h, responseType: 'blob' });
              setAvatarUrl(URL.createObjectURL(av.data));
            } catch {}
          }
        }).catch(() => setDisplayName(user.name || ''));
      Promise.all([
        axios.get(`${API_URL}/api/teacher/students`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/teacher/exams`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/teacher/assignments`, { headers: h }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/teacher/videos`, { headers: h }).catch(() => ({ data: [] })),
      ]).then(([st, ex, asg, vd]) => setCounts({
        students: st.data.length, exams: ex.data.length, assignments: asg.data.length, videos: vd.data.length,
      }));
    }
  }, [user, getAuthHeaders]);

  const saveProfile = async () => {
    if (!profileForm.name.trim()) { toast.error('الاسم مطلوب'); return; }
    setSavingProfile(true);
    try {
      await axios.put(`${API_URL}/api/profile/update`, profileForm, { headers: getAuthHeaders() });
      setDisplayName(profileForm.name.trim());
      setProfileMeta({ bio: profileForm.bio, email: profileForm.email, phone: profileForm.phone });
      toast.success('تم حفظ الملف الشخصي');
      setShowProfile(false);
    } catch { toast.error('فشل الحفظ'); }
    finally { setSavingProfile(false); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/profile/avatar`, fd, { headers: getAuthHeaders() });
      setAvatarUrl(URL.createObjectURL(file));
      toast.success('تم تحديث الصورة');
    } catch { toast.error('فشل رفع الصورة'); }
  };

  if (!user || user.role !== 'teacher') return null;

  const teacherName = displayName || user.name || '';

  return (
    <div className="tpage" dir="rtl">
      <header className="nav">
        <h1><span className="t"><GraduationCap /></span> لوحة المعلم</h1>
        <div className="us">
          <span>{teacherName}</span>
          <span className="ua" aria-hidden="true" data-testid="header-avatar">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : (teacherName.charAt(0) || 'م')}
          </span>
          <button className="lo" onClick={() => { logout(); navigate('/login'); }} aria-label="تسجيل الخروج" data-testid="logout-button"><LogOut /></button>
        </div>
      </header>

      <div className="rings" aria-hidden="true"><i></i><i></i><i></i></div>

      <main>
        <section className="cd tp" data-testid="teacher-profile-card">
          <div className="tp-b" aria-hidden="true"><i></i><i></i><i></i></div>
          <div className="tp-c">
            <div className="tp-t">
              <div className="tp-av">
                {avatarUrl ? <img src={avatarUrl} alt="" /> : (teacherName.charAt(0) || 'م')}
                <label className="tp-cam" title="تغيير الصورة" data-testid="teacher-avatar-upload">
                  <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
                  <Camera />
                </label>
              </div>
              <div className="tp-n">
                <h2 data-testid="teacher-profile-name">{teacherName}</h2>
                <small>@{user.username}</small>
                <div className="tp-ch">
                  <span className="chp" style={{ '--c': 'var(--pk)' }}><GraduationCap />معلم</span>
                  {mySubjects.slice(0, 4).map(s => <span key={s.id} className="chp">{s.name_ar}</span>)}
                </div>
              </div>
              <button className="tp-ed" onClick={() => setShowProfile(true)} data-testid="edit-profile-btn">
                <Edit3 />تعديل الملف
              </button>
            </div>
            {profileMeta.bio && <p className="tp-bio">{profileMeta.bio}</p>}
            {(profileMeta.phone || profileMeta.email || mySubjects.length > 0) && (
              <div className="tp-d">
                {profileMeta.phone && (
                  <div className="dt"><div className="t"><Phone /></div><div><span>رقم الهاتف</span><b>{profileMeta.phone}</b></div></div>
                )}
                {profileMeta.email && (
                  <div className="dt"><div className="t"><Mail /></div><div><span>البريد الإلكتروني</span><b>{profileMeta.email}</b></div></div>
                )}
                {mySubjects.length > 0 && (
                  <div className="dt"><div className="t"><BookOpen /></div><div><span>المواد التي أدرّسها</span><b style={{ direction: 'rtl', textAlign: 'right' }}>{mySubjects.map(s => s.name_ar).join('، ')}</b></div></div>
                )}
              </div>
            )}
            <div className="tp-s">
              <div className="tk" style={{ '--c': 'var(--pu)' }}><b>{counts.students}</b><span>الطلاب</span></div>
              <div className="tk" style={{ '--c': 'var(--easy)' }}><b>{counts.exams}</b><span>الامتحانات</span></div>
              <div className="tk" style={{ '--c': 'var(--med)' }}><b>{counts.assignments}</b><span>الواجبات</span></div>
              <div className="tk" style={{ '--c': 'var(--pk)' }}><b>{counts.videos}</b><span>الفيديوهات</span></div>
              <div className="tk" style={{ '--c': 'var(--hard)' }}><b>{mySubjects.length}</b><span>المواد</span></div>
            </div>
          </div>
        </section>

        <div className="tabs" role="tablist" aria-label="أقسام لوحة المعلم">
          {TABS.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} role="tab" aria-selected={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}
                className="tb" title={tab.label} data-testid={`teacher-tab-${tab.id}`}>
                <Icon /><span className="tl">{tab.label}</span>
              </button>
            );
          })}
        </div>

        <div className="pn" role="tabpanel">
          {activeTab === 'students' && <StudentsTab headers={getAuthHeaders()} subjects={mySubjects} />}
          {activeTab === 'weak-students' && <WeakStudentsTab headers={getAuthHeaders()} />}
          {activeTab === 'grades' && <GradesTab headers={getAuthHeaders()} subjects={mySubjects} />}
          {activeTab === 'weekly-plan' && <WeeklyPlanTab headers={getAuthHeaders()} subjects={mySubjects} />}
          {activeTab === 'assignments' && <AssignmentsTab headers={getAuthHeaders()} subjects={mySubjects} />}
          {activeTab === 'messages' && <PrincipalMessagesTab headers={getAuthHeaders()} />}
          {activeTab === 'minister-msgs' && <MinisterMessagesTab headers={getAuthHeaders()} />}
          {activeTab === 'videos' && <VideosTab headers={getAuthHeaders()} subjects={mySubjects} />}
          {activeTab === 'contests' && <ContestsManager headers={getAuthHeaders()} token={token} scoped={true} lightDialogs={true} />}
        </div>
      </main>

      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="tdlg max-w-lg bg-[#1a1a2e] border-white/10 text-white" dir="rtl" data-testid="edit-profile-dialog">
          <DialogDescription className="sr-only">تعديل الملف الشخصي</DialogDescription>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><User className="w-5 h-5 text-[#4da6ff]" /> تعديل الملف الشخصي</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-1">
            <div>
              <label className="text-white/60 text-xs font-bold block mb-1">الاسم</label>
              <input value={profileForm.name} onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none" data-testid="profile-name-input" />
            </div>
            <div>
              <label className="text-white/60 text-xs font-bold block mb-1">نبذة تعريفية</label>
              <textarea value={profileForm.bio} onChange={e => setProfileForm(f => ({ ...f, bio: e.target.value }))}
                placeholder="اكتب نبذة قصيرة عنك" rows={3}
                className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none resize-none" data-testid="profile-bio-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-white/60 text-xs font-bold block mb-1">البريد الإلكتروني</label>
                <input value={profileForm.email} onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                  dir="ltr" placeholder="name@example.com"
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none text-right" data-testid="profile-email-input" />
              </div>
              <div>
                <label className="text-white/60 text-xs font-bold block mb-1">رقم الهاتف</label>
                <input value={profileForm.phone} onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))}
                  dir="ltr" placeholder="05xxxxxxxx"
                  className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none text-right" data-testid="profile-phone-input" />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={saveProfile} disabled={savingProfile} className="flex-1 bg-emerald-500 hover:bg-emerald-600" data-testid="save-profile-btn">
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 ml-1" /> حفظ</>}
              </Button>
              <Button variant="ghost" onClick={() => setShowProfile(false)} className="text-white/50">إلغاء</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Student profile dialog helpers ───
const SP_TOOL = {
  'tips': ['محتوى الدرس', 'var(--med)'], 'teach-guide': ['دليل التدريس', 'var(--pu)'],
  'worksheet': ['ورقة عمل', 'var(--easy)'], 'quiz': ['اختبار', 'var(--pu)'],
  'terms': ['المصطلحات', 'var(--pk)'], 'explain-lesson': ['شرح الدرس', 'var(--pu)'],
  'story': ['القصة', 'var(--med)'], 'video': ['الفيديو', 'var(--hard)'],
};
const SP_CHAT = { 'طلب إجابة مباشرة': 'var(--hard)', 'طلب توضيح': 'var(--pu)', 'توضيح': 'var(--pu)', 'استكشاف وفضول': 'var(--easy)', 'خارج الموضوع': 'var(--med)', 'محادثة عامة': 'var(--med)' };
const spCol = p => (p < 50 ? 'var(--hard)' : p < 75 ? 'var(--med)' : 'var(--easy)');
const spToolLabel = t => (t === 'quiz' ? 'اختبار' : t === 'worksheet' ? 'ورقة عمل' : t);
function Ring({ pct, size, stroke, color, children }) {
  const r = (size - stroke) / 2, C = 2 * Math.PI * r, off = C * (1 - Math.min(pct || 0, 100) / 100), h = size / 2;
  return (
    <div className="rg" style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle className="tk2" cx={h} cy={h} r={r} strokeWidth={stroke} />
        <circle className="fl" cx={h} cy={h} r={r} strokeWidth={stroke} transform={`rotate(-90 ${h} ${h})`} style={{ stroke: color, strokeDasharray: C, strokeDashoffset: off }} />
      </svg>
      <div className="in">{children}</div>
    </div>
  );
}

// ─── Students Tab - grouped by subject ───
function StudentsTab({ headers, subjects }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [notes, setNotes] = useState([]);
  const [profileDialog, setProfileDialog] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [studentAvatar, setStudentAvatar] = useState(null);
  const [expandedSubject, setExpandedSubject] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/teacher/students`, { headers })
      .then(r => { setStudents(r.data); if (subjects.length > 0) setExpandedSubject(subjects[0]?.id); })
      .catch(() => toast.error('فشل تحميل الطلاب'))
      .finally(() => setLoading(false));
  }, [headers, subjects]);

  const openNotes = async (student) => {
    setNoteDialog(student); setNoteText('');
    const r = await axios.get(`${API_URL}/api/teacher/notes/${student.id}`, { headers });
    setNotes(r.data);
  };
  const addNote = async () => {
    if (!noteText.trim()) return;
    await axios.post(`${API_URL}/api/teacher/notes`, { student_id: noteDialog.id, note: noteText }, { headers });
    toast.success('تمت إضافة الملاحظة'); setNoteText('');
    const r = await axios.get(`${API_URL}/api/teacher/notes/${noteDialog.id}`, { headers });
    setNotes(r.data);
  };
  const deleteNote = async (noteId) => {
    await axios.delete(`${API_URL}/api/teacher/notes/${noteId}`, { headers });
    setNotes(prev => prev.filter(n => n.id !== noteId));
  };
  const openProfile = async (student) => {
    setProfileDialog(student); setProfileData(null); setStudentAvatar(null);
    try {
      const r = await axios.get(`${API_URL}/api/teacher/students/${student.id}/profile`, { headers });
      setProfileData(r.data);
      if (r.data.avatar_path) {
        try {
          const av = await axios.get(`${API_URL}/api/profile/avatar/${r.data.avatar_path}`, { headers, responseType: 'blob' });
          setStudentAvatar(URL.createObjectURL(av.data));
        } catch {}
      }
    } catch { toast.error('فشل تحميل الملف'); }
  };

  if (loading) return <div className="text-white/30 text-center py-12">جارٍ التحميل...</div>;

  return (
    <div className="space-y-4" data-testid="teacher-students">
      <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2"><Users className="w-5 h-5" /> طلابي ({students.length})</h2>

      {subjects.length === 0 ? (
        <div className="text-center py-8 text-white/30 bg-white/5 rounded-xl">لم يتم تعيين مواد لك بعد</div>
      ) : subjects.map(subj => (
        <Card key={subj.id} className="bg-white/5 border-white/10">
          <CardContent className="p-0">
            <button onClick={() => setExpandedSubject(expandedSubject === subj.id ? null : subj.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors text-right">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
                  <BookOpen className="w-4 h-4 text-emerald-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-bold">{subj.name_ar}</p>
                  <p className="text-white/30 text-[10px]">{subj.grade_name} - الفصل {subj.semester || 2}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-white/30 text-xs">{students.length} طالب</span>
                {expandedSubject === subj.id ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
              </div>
            </button>
            {expandedSubject === subj.id && (
              <div className="px-4 pb-4 space-y-2">
                {students.length === 0 ? (
                  <p className="text-white/30 text-center text-sm py-4">لا يوجد طلاب</p>
                ) : students.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-2.5 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2.5">
                      <div className="w-7 h-7 rounded-full bg-[#4da6ff]/20 flex items-center justify-center shrink-0">
                        <span className="text-[9px] text-[#4da6ff] font-bold">{s.grade_number || '-'}</span>
                      </div>
                      <div>
                        <p className="text-white text-xs font-medium">{s.name}</p>
                        <p className="text-white/30 text-[10px]">{s.username}</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <button onClick={() => openProfile(s)} className="px-2 py-1 rounded-md bg-[#4da6ff]/10 text-[#4da6ff] text-[10px] hover:bg-[#4da6ff]/20">
                        <BarChart3 className="w-3 h-3 inline ml-0.5" />الملف
                      </button>
                      <button onClick={() => openNotes(s)} className="px-2 py-1 rounded-md bg-amber-500/10 text-amber-400 text-[10px] hover:bg-amber-500/20">
                        <StickyNote className="w-3 h-3 inline ml-0.5" />ملاحظة
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Notes Dialog */}
      <Dialog open={!!noteDialog} onOpenChange={() => setNoteDialog(null)}>
        <DialogContent className="tdlg max-w-lg bg-[#1a1a2e] border-white/10 text-white" dir="rtl">
          <DialogDescription className="sr-only">ملاحظات</DialogDescription>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><StickyNote className="w-5 h-5 text-amber-400" /> ملاحظات عن {noteDialog?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="اكتب ملاحظة..."
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none"
                onKeyDown={e => e.key === 'Enter' && addNote()} data-testid="note-input" />
              <Button size="sm" onClick={addNote} className="bg-amber-500 hover:bg-amber-600"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {notes.map(n => (
                <div key={n.id} className="p-3 rounded-lg bg-white/5 border border-white/10 flex justify-between items-start gap-2">
                  <div><p className="text-white/80 text-sm">{n.note}</p><p className="text-white/30 text-[10px] mt-1">{new Date(n.created_at).toLocaleDateString('ar')}</p></div>
                  <button onClick={() => deleteNote(n.id)} className="text-red-400/50 hover:text-red-400 shrink-0"><Trash2 className="w-3 h-3" /></button>
                </div>
              ))}
              {notes.length === 0 && <p className="text-white/30 text-center text-sm py-4">لا توجد ملاحظات</p>}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog - Comprehensive (teacher view) */}
      <Dialog open={!!profileDialog} onOpenChange={() => setProfileDialog(null)}>
        <DialogContent className="tdlg spw bg-[#1a1a2e] border-white/10 text-white" dir="rtl" data-testid="student-profile-dialog">
          <DialogDescription className="sr-only">ملف الطالب الشامل</DialogDescription>
          <div className="sp-h">
            <div className="sp-av">{studentAvatar ? <img src={studentAvatar} alt="" /> : (profileDialog?.name?.charAt(0) || '؟')}</div>
            <div className="sp-t">
              <DialogTitle asChild><h2>{profileDialog?.name}</h2></DialogTitle>
              <p><bdi>@{profileDialog?.username}</bdi> · الصف {profileDialog?.grade_number || '-'}</p>
            </div>
            <div className="sp-ac">
              <button className="bt am sm" onClick={() => { setProfileDialog(null); openNotes(profileDialog); }}>
                <StickyNote style={{ width: 15, height: 15 }} />ملاحظة
              </button>
            </div>
          </div>

          {!profileData ? <div className="sp-empty">جارٍ التحميل...</div> : (() => {
            const st = profileData.stats || {};
            const lv = st.level;
            const lvPct = lv?.next_xp ? Math.min(100, Math.round((lv.xp / lv.next_xp) * 100)) : 100;
            const avg = st.avg_score_pct || 0;
            const ol = st.olympics;
            const ti = st.teacher_interaction;
            const tbs = st.tools_by_subject || {};
            return (
              <div className="sp-b">
                {/* Sidebar */}
                <div className="sp-s">
                  {lv && (
                    <div className="sc2 lv2">
                      <Ring pct={lvPct} size={128} stroke={10} color="var(--pk)">
                        <span className="big">{lv.num}</span><span className="sm">المستوى</span>
                      </Ring>
                      <b className="nm">{lv.name}</b>
                      <small>{lv.xp} نقطة خبرة{lv.next_xp ? ` · ${lv.next_xp - lv.xp} للمستوى التالي` : ''}</small>
                    </div>
                  )}
                  {avg > 0 && (
                    <div className="sc2 ov2">
                      <Ring pct={avg} size={84} stroke={8} color={spCol(avg)}>
                        <b style={{ fontSize: 19, fontWeight: 800, color: spCol(avg) }}>{avg}%</b>
                      </Ring>
                      <div><div className="t">المعدل العام</div><small>متوسط نتائج كل المواد</small></div>
                    </div>
                  )}
                  <div className="st4">
                    <div className="k4" style={{ '--c': 'var(--pu)' }}><BookOpen /><b>{profileData.total_lessons_touched || 0}</b><span>دروس</span></div>
                    <div className="k4" style={{ '--c': 'var(--easy)' }}><CheckCircle /><b>{profileData.total_tools_completed || 0}</b><span>أنشطة</span></div>
                    <div className="k4" style={{ '--c': 'var(--med)' }}><Target /><b>{st.total_attempts || 0}</b><span>اختبارات</span></div>
                    <div className="k4" style={{ '--c': 'var(--pk)' }}><CalendarDays /><b>{st.active_days || 0}</b><span>أيام نشاط</span></div>
                  </div>
                </div>

                {/* Main */}
                <div className="sp-m">
                  {/* Subject performance */}
                  {st.subject_stats?.length > 0 && (
                    <div className="sc2" style={{ '--c': 'var(--pu)' }}>
                      <div className="hd2"><BookOpen />أداء المواد</div>
                      <div className="sbj">
                        {st.subject_stats.map((subj, i) => {
                          const p = subj.avg_pct ?? subj.pct ?? 0;
                          const c = spCol(p);
                          const tools = tbs[subj.name];
                          return (
                            <div className="sj" key={i} style={{ '--c': c }}>
                              <Ring pct={p} size={56} stroke={6} color={c}><b>{p}%</b></Ring>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <h4>{subj.name}</h4>
                                <div className="cps">
                                  {tools ? Object.entries(tools).sort((a, b) => b[1] - a[1]).map(([t, cnt]) => {
                                    const m = SP_TOOL[t] || [t, 'var(--pu)'];
                                    return <span className="cp" key={t} style={{ '--c': m[1] }}><em>{cnt}</em>{m[0]}</span>;
                                  }) : <span className="cp"><em>{subj.attempts || 0}</em>محاولة</span>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Olympics */}
                  {ol?.subjects?.length > 0 && (
                    <div className="sc2 olm" style={{ '--c': 'var(--pk)' }}>
                      <div className="hd2" style={{ '--c': 'var(--pk)' }}><Trophy />الأولمبياد</div>
                      <div className="mm">
                        <div><b style={{ color: 'var(--easy)' }}>{ol.total_correct}</b><span>صحيحة</span></div>
                        <div><b style={{ color: 'var(--hard)' }}>{ol.total_wrong}</b><span>خاطئة</span></div>
                        <div><b style={{ color: 'var(--pu)' }}>{ol.total_attempts}</b><span>محاولات</span></div>
                        <div><b style={{ color: 'var(--pk)' }}>{ol.total_time}ث</b><span>الوقت</span></div>
                      </div>
                      {ol.subjects.map(s => (
                        <div className="ol-r" key={s.subject_id}>
                          <span>{s.name_ar}</span>
                          <span style={{ color: 'var(--easy)' }}>{s.correct}/{s.total_questions}</span>
                          {s.rank > 0 && <span className="rk">#{s.rank}</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Chat analysis */}
                  {st.chat_analysis && (
                    <div className="sc2" style={{ '--c': 'var(--pu)' }}>
                      <div className="hd2"><MessageSquare />تحليل المحادثات<small>{st.chat_analysis.total_messages} رسالة</small></div>
                      {st.chat_analysis.categories.map(cat => (
                        <div className="ch2" key={cat.name} style={{ '--c': SP_CHAT[cat.name] || 'var(--pu)' }}>
                          <i></i><span className="nm">{cat.name}</span><span className="pc">{cat.count} ({cat.pct}%)</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reading / interaction */}
                  {ti && (
                    <div className="sc2" style={{ '--c': 'var(--pu)' }}>
                      <div className="hd2"><Eye />متابعة القراءة والاطلاع</div>
                      <div className="rd2">
                        <div className="rt" style={{ '--c': 'var(--easy)' }}>
                          <Ring pct={ti.assignments_total ? (ti.assignments_read / ti.assignments_total) * 100 : 0} size={44} stroke={5} color="var(--easy)">
                            <b>{ti.assignments_read}/{ti.assignments_total}</b>
                          </Ring>
                          <div><div className="t">{ti.assignments_read}/{ti.assignments_total}</div><small>واجبات مقروءة</small></div>
                        </div>
                        <div className="rt" style={{ '--c': 'var(--pu)' }}>
                          <Ring pct={ti.plans_total ? (ti.plans_seen / ti.plans_total) * 100 : 0} size={44} stroke={5} color="var(--pu)">
                            <b>{ti.plans_seen}/{ti.plans_total}</b>
                          </Ring>
                          <div><div className="t">{ti.plans_seen}/{ti.plans_total}</div><small>خطط تم الاطلاع عليها</small></div>
                        </div>
                      </div>
                      {ti.assignments?.length > 0 && (<>
                        <div className="lb2">الواجبات</div>
                        {ti.assignments.map(a => (
                          <div className={`rr ${a.is_read ? 'ok' : 'no'}`} key={a.id}>
                            {a.is_read ? <CheckCircle /> : <XCircle />}
                            <span className="nm">{a.title}</span>
                            {a.subject_name && <span className="sj2">{a.subject_name}</span>}
                            <span className="s">{a.is_read ? 'قرأها' : 'لم يقرأها'}</span>
                          </div>
                        ))}
                      </>)}
                      {ti.weekly_plans?.length > 0 && (<>
                        <div className="lb2">الخطط الأسبوعية</div>
                        {ti.weekly_plans.map(p => (
                          <div className={`rr ${p.is_seen ? 'ok' : 'no'}`} key={p.id}>
                            {p.is_seen ? <CheckCircle /> : <XCircle />}
                            <span className="nm">{p.week_label || 'خطة أسبوعية'}</span>
                            <span className="s">{p.is_seen ? 'اطلع عليها' : 'لم يطلع'}</span>
                          </div>
                        ))}
                      </>)}
                      {ti.notes?.length > 0 && (<>
                        <div className="lb2">ملاحظات المعلمين</div>
                        {ti.notes.map(n => (
                          <div className="nt2" key={n.id}><p>{n.note}</p><small>{n.teacher_name} · {new Date(n.created_at).toLocaleDateString('ar')}</small></div>
                        ))}
                      </>)}
                    </div>
                  )}

                  {/* Lessons progress */}
                  <div className="sc2" style={{ '--c': 'var(--pu)' }}>
                    <div className="hd2"><BookOpen />تقدم الدروس<small>{profileData.lessons?.length || 0}</small></div>
                    {profileData.lessons?.length > 0 ? (
                      <div className="ls3">
                        {profileData.lessons.map(l => (
                          <div className="lz" key={l.lesson_id} style={{ '--c': 'var(--pu)' }}>
                            <div className="lz-h">
                              <div><h4>{l.title}</h4><p>{l.grade_name}{l.subject_name ? ` · ${l.subject_name}` : ''}</p></div>
                              <span className="act">{l.completed_tools.length} نشاط</span>
                            </div>
                            {l.scores?.map((s, i) => {
                              const pct = s.total > 0 ? Math.round((s.score / s.total) * 100) : 0;
                              const c = spCol(pct);
                              return (
                                <div className="sc3" key={i} style={{ '--c': c }}>
                                  <span>{spToolLabel(s.tool_type)}</span>
                                  <span className="pb"><i style={{ width: `${pct}%`, background: c }} /></span>
                                  <b>{s.score}/{s.total}</b>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    ) : <div className="sp-empty" style={{ padding: 20 }}>لم يبدأ أي درس بعد</div>}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Weak Students Tab (الطلاب الذين يحتاجون متابعة) ───
function WeakStudentsTab({ headers }) {
  const [weakStudents, setWeakStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/teacher/weak-students`, { headers })
      .then(r => setWeakStudents(r.data)).catch(() => {})
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="text-white/30 text-center py-12">جارٍ التحميل...</div>;

  return (
    <div className="space-y-4" data-testid="teacher-weak-students">
      <h2 className="text-lg font-bold text-red-400 flex items-center gap-2">
        <AlertTriangle className="w-5 h-5" /> الطلاب الذين يحتاجون متابعة ({weakStudents.length})
      </h2>

      {weakStudents.length === 0 ? (
        <div className="text-center py-12 bg-emerald-500/5 border border-emerald-500/20 rounded-2xl">
          <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
          <p className="text-emerald-400 text-sm font-bold">جميع الطلاب بحالة جيدة</p>
          <p className="text-white/30 text-xs mt-1">لا يوجد طلاب يحتاجون متابعة حالياً</p>
        </div>
      ) : (
        <div className="space-y-3">
          {weakStudents.map(s => (
            <Card key={s.id} className="bg-red-500/5 border-red-500/15 hover:border-red-500/30 transition-all">
              <CardContent className="p-0">
                <button
                  onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                  className="w-full p-4 text-right hover:bg-white/[0.02] transition-colors"
                  data-testid={`weak-student-${s.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      </div>
                      <div>
                        <p className="text-white text-sm font-bold">{s.name}</p>
                        <p className="text-white/30 text-[10px]">@{s.username} - الصف {s.grade_number || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {s.avg_grade > 0 && (
                        <span className={`text-sm font-bold ${s.avg_grade >= 80 ? 'text-emerald-400' : s.avg_grade >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                          {s.avg_grade}%
                        </span>
                      )}
                      <span className="text-red-400/60 text-[10px] px-2 py-0.5 rounded-full bg-red-500/10">
                        {s.reasons.length} ملاحظة
                      </span>
                      {expanded === s.id ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                    </div>
                  </div>
                </button>

                {expanded === s.id && (
                  <div className="px-4 pb-4 space-y-3">
                    {/* Reasons */}
                    <div className="p-3 rounded-xl bg-red-500/5 border border-red-500/10">
                      <p className="text-red-400 text-xs font-bold mb-2">أسباب المتابعة:</p>
                      <div className="space-y-1.5">
                        {s.reasons.map((r, ri) => (
                          <div key={ri} className="flex items-start gap-2 text-xs">
                            <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
                            <span className="text-red-300/80">{r}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Subject Performance */}
                    {s.subjects_performance?.length > 0 && (
                      <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                        <p className="text-white/60 text-xs font-bold mb-2">أداء المواد الدراسية:</p>
                        <div className="space-y-2.5">
                          {s.subjects_performance.map((subj, si) => (
                            <div key={si} className="p-2.5 rounded-lg bg-white/[0.02] border border-white/5">
                              <div className="flex items-center justify-between mb-1.5">
                                <span className="text-white text-xs font-medium">{subj.name}</span>
                                <div className="flex items-center gap-2 text-[10px]">
                                  <span className={`font-bold ${subj.avg_pct >= 80 ? 'text-emerald-400' : subj.avg_pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                                    {subj.avg_pct}%
                                  </span>
                                  {subj.failed_count > 0 && (
                                    <span className="text-red-400">{subj.failed_count} رسوب</span>
                                  )}
                                  {subj.missing > 0 && (
                                    <span className="text-amber-400">{subj.missing} لم يتقدم</span>
                                  )}
                                </div>
                              </div>
                              {/* Progress bar */}
                              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-2">
                                <div className={`h-full rounded-full ${subj.avg_pct >= 80 ? 'bg-emerald-500' : subj.avg_pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${Math.min(100, subj.avg_pct)}%` }} />
                              </div>
                              {/* Per-exam details */}
                              {subj.exams?.length > 0 && (
                                <div className="space-y-1">
                                  {subj.exams.map((e, ei) => (
                                    <div key={ei} className="flex items-center justify-between text-[10px] py-1 px-2 rounded bg-white/[0.02]">
                                      <div className="flex items-center gap-1.5">
                                        <FileSpreadsheet className="w-3 h-3 text-white/20" />
                                        <span className="text-white/50">{e.exam_title}</span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className={`font-bold ${e.pct >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                                          {e.mark}/{e.max_mark}
                                        </span>
                                        <span className={e.pct >= 50 ? 'text-emerald-400' : 'text-red-400'}>
                                          ({e.pct}%)
                                        </span>
                                        {e.pct < 50 ? (
                                          <XCircle className="w-3 h-3 text-red-400" />
                                        ) : (
                                          <CheckCircle className="w-3 h-3 text-emerald-400" />
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Activity info */}
                    <div className="flex items-center gap-3 text-[10px] text-white/30">
                      <span>{s.lessons_touched} درس مدروس</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}



// ─── Grades Tab (كشف العلامات) ───
function GradesTab({ headers, subjects }) {
  const [students, setStudents] = useState([]);
  const [exams, setExams] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', subject_id: '', subject_name: '', max_mark: 100 });
  const [activeExam, setActiveExam] = useState(null);
  const [grades, setGrades] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchExams = useCallback(() => {
    axios.get(`${API_URL}/api/teacher/exams`, { headers }).then(r => setExams(r.data)).catch(() => {});
  }, [headers]);

  useEffect(() => {
    fetchExams();
    axios.get(`${API_URL}/api/teacher/students`, { headers }).then(r => setStudents(r.data)).catch(() => {});
  }, [headers, fetchExams]);

  const handleAddExam = async () => {
    if (!form.title.trim()) return;
    await axios.post(`${API_URL}/api/teacher/exams`, form, { headers });
    toast.success('تم إنشاء الامتحان');
    setForm({ title: '', subject_id: '', subject_name: '', max_mark: 100 });
    setShowAdd(false);
    fetchExams();
  };

  const handleDeleteExam = async (id) => {
    if (!window.confirm('حذف هذا الامتحان وجميع علاماته؟')) return;
    await axios.delete(`${API_URL}/api/teacher/exams/${id}`, { headers });
    toast.success('تم الحذف');
    if (activeExam?.id === id) setActiveExam(null);
    fetchExams();
  };

  const openExam = async (exam) => {
    setActiveExam(exam);
    const r = await axios.get(`${API_URL}/api/teacher/exams/${exam.id}/grades`, { headers });
    const gradesMap = {};
    r.data.forEach(g => { gradesMap[g.student_id] = g.mark; });
    setGrades(gradesMap);
  };

  const handleSaveGrades = async () => {
    if (!activeExam) return;
    setSaving(true);
    const gradesList = students.map(s => ({
      student_id: s.id,
      student_name: s.name,
      mark: grades[s.id] !== undefined && grades[s.id] !== '' ? Number(grades[s.id]) : null
    })).filter(g => g.mark !== null);
    try {
      await axios.post(`${API_URL}/api/teacher/exams/${activeExam.id}/grades`, { grades: gradesList }, { headers });
      toast.success(`تم حفظ ${gradesList.length} علامة`);
    } catch { toast.error('فشل الحفظ'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="teacher-grades">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" /> كشف العلامات</h2>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="add-exam-btn"><Plus className="w-4 h-4 ml-1" /> امتحان جديد</Button>
      </div>

      {showAdd && (
        <Card className="bg-white/5 border-emerald-500/30">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="عنوان الامتحان"
                className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="exam-title" />
              <select value={form.subject_id} onChange={e => { const s = subjects.find(x => x.id === e.target.value); setForm(f => ({ ...f, subject_id: e.target.value, subject_name: s?.name_ar || '' })); }}
                className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="exam-subject">
                <option value="">اختر المادة</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name_ar} - {s.grade_name}</option>)}
              </select>
              <input type="number" value={form.max_mark} onChange={e => setForm(f => ({ ...f, max_mark: Number(e.target.value) }))} placeholder="العلامة القصوى"
                className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="exam-max" />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleAddExam} className="bg-emerald-500">إنشاء</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowAdd(false)} className="text-white/50">إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Exams List */}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {exams.map(e => (
          <Card key={e.id} className={`border cursor-pointer transition-all ${activeExam?.id === e.id ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-white/5 border-white/10 hover:bg-white/10'}`}
            onClick={() => openExam(e)}>
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white text-sm font-medium">{e.title}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {e.subject_name && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{e.subject_name}</span>}
                    <span className="text-white/30 text-[10px]">{e.max_mark} علامة</span>
                    <span className="text-white/30 text-[10px]">{e.grades_count} طالب</span>
                  </div>
                </div>
                <button onClick={(ev) => { ev.stopPropagation(); handleDeleteExam(e.id); }} className="text-red-400/40 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {exams.length === 0 && !showAdd && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لم يتم إنشاء أي امتحان بعد</div>}

      {/* Grade Entry Sheet */}
      {activeExam && (
        <Card className="bg-white/5 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-bold text-sm">{activeExam.title}</h3>
                <p className="text-white/30 text-[10px]">{activeExam.subject_name} - العلامة القصوى: {activeExam.max_mark}</p>
              </div>
              <Button onClick={handleSaveGrades} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-sm" data-testid="save-grades-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 ml-1" /> حفظ العلامات</>}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[400px]">
                <thead>
                  <tr>
                    <th className="p-2 text-right text-white/50 text-xs border border-white/10 bg-white/5 w-8">#</th>
                    <th className="p-2 text-right text-white/50 text-xs border border-white/10 bg-white/5">اسم الطالب</th>
                    <th className="p-2 text-center text-white/50 text-xs border border-white/10 bg-white/5 w-28">العلامة / {activeExam.max_mark}</th>
                    <th className="p-2 text-center text-white/50 text-xs border border-white/10 bg-white/5 w-16">%</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, idx) => {
                    const mark = grades[s.id];
                    const pct = mark !== undefined && mark !== '' ? Math.round((Number(mark) / activeExam.max_mark) * 100) : null;
                    return (
                      <tr key={s.id} className="hover:bg-white/5">
                        <td className="p-2 text-center text-white/30 text-xs border border-white/10">{idx + 1}</td>
                        <td className="p-2 text-white text-xs border border-white/10">
                          <span className="font-medium">{s.name}</span>
                          <span className="text-white/30 text-[10px] mr-2">{s.username}</span>
                        </td>
                        <td className="p-1 border border-white/10">
                          <input type="number" min="0" max={activeExam.max_mark}
                            value={mark !== undefined && mark !== null ? mark : ''}
                            onChange={e => setGrades(prev => ({ ...prev, [s.id]: e.target.value }))}
                            className="w-full bg-transparent text-center text-white text-sm py-1 focus:outline-none focus:bg-white/5 rounded"
                            placeholder="-"
                            data-testid={`grade-${s.id}`} />
                        </td>
                        <td className="p-2 text-center border border-white/10">
                          {pct !== null ? (
                            <span className={`text-xs font-bold ${pct >= 80 ? 'text-emerald-400' : pct >= 50 ? 'text-amber-400' : 'text-red-400'}`}>{pct}%</span>
                          ) : <span className="text-white/20 text-xs">-</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}


// ─── Weekly Plan Tab ───
function WeeklyPlanTab({ headers, subjects }) {
  const emptyDays = () => DAYS.map(d => ({ day: d, periods: PERIODS.map(() => ({ subject: '', topic: '' })) }));
  const [plan, setPlan] = useState({ week_label: '', days: emptyDays(), period_labels: [...PERIODS] });
  const [saving, setSaving] = useState(false);
  const [showArchive, setShowArchive] = useState(false);
  const [archive, setArchive] = useState([]);
  const [viewingPlan, setViewingPlan] = useState(null);

  const subjectNames = subjects.map(s => s.name_ar);

  useEffect(() => {
    axios.get(`${API_URL}/api/teacher/weekly-plan`, { headers }).then(r => {
      if (r.data && r.data.days) {
        const savedDays = r.data.days;
        const fullDays = DAYS.map(dayName => {
          const existing = savedDays.find(d => d.day === dayName);
          if (existing) {
            const periods = PERIODS.map((_, pi) => existing.periods?.[pi] || { subject: '', topic: '' });
            return { day: dayName, periods };
          }
          return { day: dayName, periods: PERIODS.map(() => ({ subject: '', topic: '' })) };
        });
        setPlan({ ...r.data, days: fullDays, period_labels: r.data.period_labels || [...PERIODS] });
      }
    }).catch(() => {});
  }, [headers]);

  const updateCell = (dayIdx, periodIdx, field, value) => {
    setPlan(prev => {
      const newDays = [...prev.days];
      newDays[dayIdx] = { ...newDays[dayIdx], periods: [...newDays[dayIdx].periods] };
      newDays[dayIdx].periods[periodIdx] = { ...newDays[dayIdx].periods[periodIdx], [field]: value };
      return { ...prev, days: newDays };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try { await axios.post(`${API_URL}/api/teacher/weekly-plan`, plan, { headers }); toast.success('تم حفظ الخطة الأسبوعية'); }
    catch { toast.error('فشل الحفظ'); }
    finally { setSaving(false); }
  };

  const handleNewPlan = () => { setPlan({ week_label: '', days: emptyDays(), period_labels: [...PERIODS] }); };

  const loadArchive = async () => {
    setShowArchive(true);
    const r = await axios.get(`${API_URL}/api/teacher/weekly-plans/archive`, { headers });
    setArchive(r.data);
  };

  return (
    <div className="space-y-4" data-testid="teacher-weekly-plan">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2"><CalendarDays className="w-5 h-5" /> الخطة الأسبوعية</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={plan.week_label} onChange={e => setPlan(p => ({ ...p, week_label: e.target.value }))}
            placeholder="عنوان الأسبوع (مثال: الأسبوع 15)"
            className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none w-48" data-testid="week-label" />
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-sm" data-testid="save-plan-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'حفظ'}
          </Button>
          <Button variant="ghost" onClick={handleNewPlan} className="text-[#4da6ff] hover:bg-[#4da6ff]/10 text-sm"><Plus className="w-4 h-4 ml-1" /> جديدة</Button>
          <Button variant="ghost" onClick={loadArchive} className="text-white/40 hover:bg-white/5 text-sm"><Archive className="w-4 h-4 ml-1" /> الأرشيف</Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[700px]">
          <thead>
            <tr>
              <th className="p-2 text-right text-white/40 text-xs border border-white/10 bg-white/5 w-20"></th>
              {DAYS.map(d => <th key={d} className="p-2 text-center text-white/70 text-xs border border-white/10 bg-white/5 font-bold">{d}</th>)}
            </tr>
          </thead>
          <tbody>
            {PERIODS.map((period, pi) => (
              <tr key={pi}>
                <td className="p-1 border border-white/10 bg-white/5">
                  <input value={plan.period_labels?.[pi] || period} onChange={e => {
                    setPlan(prev => {
                      const labels = [...(prev.period_labels || [...PERIODS])];
                      labels[pi] = e.target.value;
                      return { ...prev, period_labels: labels };
                    });
                  }} className="w-full bg-transparent text-center text-white/50 text-[10px] font-medium focus:outline-none focus:bg-white/5 rounded px-1 py-0.5" />
                </td>
                {plan.days.map((day, di) => (
                  <td key={di} className="p-1 border border-white/10">
                    <select value={day.periods[pi]?.subject || ''} onChange={e => updateCell(di, pi, 'subject', e.target.value)}
                      className="w-full bg-transparent border-none text-white text-[11px] px-1 py-0.5 focus:outline-none focus:bg-white/5 rounded">
                      <option value="">المادة</option>
                      {subjectNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <input value={day.periods[pi]?.topic || ''} onChange={e => updateCell(di, pi, 'topic', e.target.value)}
                      placeholder="الموضوع" className="w-full bg-transparent border-none text-white/50 text-[10px] px-1 py-0.5 focus:outline-none focus:bg-white/5 rounded placeholder:text-white/10" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Archive Dialog */}
      <Dialog open={showArchive} onOpenChange={setShowArchive}>
        <DialogContent className="tdlg max-w-2xl bg-[#1a1a2e] border-white/10 text-white max-h-[80vh] overflow-y-auto" dir="rtl">
          <DialogDescription className="sr-only">أرشيف الخطط</DialogDescription>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Archive className="w-5 h-5 text-amber-400" /> أرشيف الخطط الأسبوعية</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {archive.map(p => (
              <div key={p.id} className="flex items-center gap-2 p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                <button onClick={() => { setViewingPlan(p); setShowArchive(false); }} className="flex-1 text-right">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-medium text-sm">{p.week_label || 'بدون عنوان'}</span>
                    <span className="text-white/30 text-[10px]">{new Date(p.created_at).toLocaleDateString('ar')}</span>
                  </div>
                </button>
                <button onClick={async () => {
                  if (!window.confirm('حذف هذه الخطة؟')) return;
                  await axios.delete(`${API_URL}/api/teacher/weekly-plans/${p.id}`, { headers });
                  setArchive(prev => prev.filter(x => x.id !== p.id));
                  toast.success('تم الحذف');
                }} className="text-red-400/40 hover:text-red-400 shrink-0 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {archive.length === 0 && <p className="text-white/30 text-center py-8">لا يوجد أرشيف</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* View archived plan */}
      <Dialog open={!!viewingPlan} onOpenChange={() => setViewingPlan(null)}>
        <DialogContent className="tdlg max-w-3xl bg-[#1a1a2e] border-white/10 text-white max-h-[80vh] overflow-x-auto" dir="rtl">
          <DialogDescription className="sr-only">عرض خطة</DialogDescription>
          <DialogHeader><DialogTitle>{viewingPlan?.week_label || 'خطة أسبوعية'} - {viewingPlan?.created_at && new Date(viewingPlan.created_at).toLocaleDateString('ar')}</DialogTitle></DialogHeader>
          {viewingPlan && (
            <table className="w-full border-collapse min-w-[600px] text-[10px]">
              <thead><tr><th className="p-1 border border-white/10 bg-white/5"></th>{viewingPlan.days?.map(d => <th key={d.day} className="p-1 border border-white/10 bg-white/5 text-white/60">{d.day}</th>)}</tr></thead>
              <tbody>
                {PERIODS.map((_, pi) => (
                  <tr key={pi}><td className="p-1 border border-white/10 bg-white/5 text-white/40 text-center">{viewingPlan.period_labels?.[pi] || `ح${pi+1}`}</td>
                    {viewingPlan.days?.map((d, di) => <td key={di} className="p-1 border border-white/10">{d.periods?.[pi]?.subject && <span className="text-white/70 block">{d.periods[pi].subject}</span>}{d.periods?.[pi]?.topic && <span className="text-white/40 block">{d.periods[pi].topic}</span>}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Button size="sm" onClick={() => { setPlan(viewingPlan); setViewingPlan(null); toast.success('تم تحميل الخطة للتعديل'); }} className="bg-[#4da6ff]">تحميل للتعديل</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Assignments Tab ───
function AssignmentsTab({ headers, subjects }) {
  const [assignments, setAssignments] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', due_date: '', subject_id: '', subject_name: '' });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('rasheedgpt_token');

  const fetchAssignments = useCallback(() => {
    axios.get(`${API_URL}/api/teacher/assignments`, { headers }).then(r => setAssignments(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  const handleAdd = async () => {
    if (!form.title.trim()) { toast.error('عنوان المهمة مطلوب'); return; }
    try {
      setUploading(true);
      // Create assignment first
      const resp = await axios.post(`${API_URL}/api/teacher/assignments`, form, { headers });
      const assignmentId = resp.data.id;

      // Upload files if any
      if (files.length > 0) {
        const formData = new FormData();
        files.forEach((f, i) => formData.append(`file_${i}`, f));
        const up = await axios.post(`${API_URL}/api/teacher/assignments/${assignmentId}/upload-files`, formData, {
          headers,
        });
        const uploadedCount = up.data?.files?.length || 0;
        if (uploadedCount < files.length) {
          toast.warning(`تم إنشاء المهمة لكن لم تُرفع كل الملفات (${uploadedCount}/${files.length})`);
        } else {
          toast.success(`تمت إضافة المهمة مع ${uploadedCount} ملف`);
        }
      } else {
        toast.success('تمت إضافة المهمة');
      }
      setForm({ title: '', description: '', due_date: '', subject_id: '', subject_name: '' });
      setFiles([]);
      setShowAdd(false);
      fetchAssignments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'فشل إضافة المهمة');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل تريد حذف هذه المهمة؟')) return;
    await axios.delete(`${API_URL}/api/teacher/assignments/${id}`, { headers });
    toast.success('تم الحذف'); fetchAssignments();
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (name) => {
    const ext = name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'text-red-400';
    if (['doc', 'docx'].includes(ext)) return 'text-blue-400';
    if (['xls', 'xlsx'].includes(ext)) return 'text-emerald-400';
    return 'text-white/50';
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4" data-testid="teacher-assignments">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2"><ClipboardList className="w-5 h-5" /> الواجبات والمهام</h2>
        <Button onClick={() => setShowAdd(true)} className="bg-emerald-500 hover:bg-emerald-600" data-testid="add-assignment-btn"><Plus className="w-4 h-4 ml-1" /> إضافة مهمة</Button>
      </div>

      {showAdd && (
        <Card className="bg-white/5 border-emerald-500/30">
          <CardContent className="p-4 space-y-3">
            <select value={form.subject_id} onChange={e => { const s = subjects.find(x => x.id === e.target.value); setForm(f => ({ ...f, subject_id: e.target.value, subject_name: s?.name_ar || '' })); }}
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="assignment-subject">
              <option value="">اختر المادة</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name_ar} - {s.grade_name}</option>)}
            </select>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="عنوان المهمة"
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="assignment-title" />
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="الوصف والتفاصيل"
              className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none h-20 resize-none" data-testid="assignment-desc" />
            <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
              className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="assignment-due" />

            {/* File Upload */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <Paperclip className="w-4 h-4 text-emerald-400" /> إرفاق ملفات (PDF, Word, Excel, صور)
              </div>
              <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg"
                onChange={e => setFiles(Array.from(e.target.files))}
                data-testid="assignment-file-input"
                className="w-full text-xs text-white/60 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 file:bg-emerald-500/20 file:text-emerald-400 file:border-0 file:rounded-lg file:px-3 file:py-1.5 file:ml-2 file:cursor-pointer cursor-pointer" />
              {files.length > 0 && (
                <div className="space-y-1.5">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10">
                      <FileIcon className={`w-4 h-4 shrink-0 ${getFileIcon(f.name)}`} />
                      <span className="text-white/80 text-xs truncate flex-1">{f.name}</span>
                      <span className="text-white/30 text-[10px] shrink-0">{formatSize(f.size)}</span>
                      <button onClick={() => removeFile(i)} className="text-red-400/50 hover:text-red-400 shrink-0">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button size="sm" onClick={handleAdd} disabled={uploading} className="bg-emerald-500" data-testid="submit-assignment-btn">
                {uploading ? <><Loader2 className="w-4 h-4 ml-1 animate-spin" /> جارٍ الرفع...</> : 'إضافة'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowAdd(false); setFiles([]); }} className="text-white/50">إلغاء</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {assignments.map(a => (
          <Card key={a.id} className="bg-white/5 border-white/10">
            <CardContent className="p-4">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {a.subject_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{a.subject_name}</span>}
                    <p className="text-white font-medium text-sm">{a.title}</p>
                  </div>
                  {a.description && <p className="text-white/50 text-xs mt-1">{a.description}</p>}

                  {/* Attached Files */}
                  {a.files?.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {a.files.map((f, idx) => (
                        <a key={idx} href={`${API_URL}/api/assignments/${a.id}/download/${idx}?token=${token}`}
                          target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:border-emerald-500/30 transition-colors group w-fit"
                          data-testid={`file-download-${a.id}-${idx}`}>
                          <FileIcon className={`w-3.5 h-3.5 shrink-0 ${getFileIcon(f.filename)}`} />
                          <span className="text-white/60 text-[11px] group-hover:text-white/80">{f.filename}</span>
                          <Download className="w-3 h-3 text-emerald-400/50 group-hover:text-emerald-400" />
                        </a>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center gap-3 mt-2 text-[10px] text-white/30">
                    {a.due_date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {a.due_date}</span>}
                    <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {a.read_count || 0} قرأها</span>
                    {a.files?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {a.files.length} ملف</span>}
                    <span>{new Date(a.created_at).toLocaleDateString('ar')}</span>
                  </div>
                </div>
                <button onClick={() => handleDelete(a.id)} className="text-red-400/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </CardContent>
          </Card>
        ))}
        {assignments.length === 0 && <div className="text-white/30 text-center py-8">لا توجد مهام</div>}
      </div>
    </div>
  );
}


// ─── Principal Messages Tab ───
function PrincipalMessagesTab({ headers }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(() => {
    axios.get(`${API_URL}/api/teacher/my-messages`, { headers })
      .then(r => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [headers]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/teacher/my-messages/${id}/read`, {}, { headers });
      setMessages(prev => prev.map(m => m.id === id ? { ...m, read_at: new Date().toISOString() } : m));
      toast.success('تم تحديد كمقروء');
    } catch { toast.error('فشل'); }
  };

  if (loading) return <div className="text-white/30 text-center py-12">جارٍ التحميل...</div>;
  const unread = messages.filter(m => !m.read_at).length;

  return (
    <div className="space-y-4" data-testid="teacher-principal-messages">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-indigo-400 flex items-center gap-2">
          <Mail className="w-5 h-5" /> رسائل مدير المدرسة ({messages.length})
        </h2>
        {unread > 0 && (
          <span className="text-[10px] px-3 py-1 rounded-full bg-red-500/20 text-red-400 font-bold">{unread} غير مقروءة</span>
        )}
      </div>

      <div className="space-y-2">
        {messages.map(m => (
          <Card key={m.id} className={`border-white/10 transition-all ${m.read_at ? 'bg-white/[0.03]' : 'bg-indigo-500/5 border-indigo-500/20'}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${m.read_at ? 'bg-white/5' : 'bg-indigo-500/20'}`}>
                    {m.read_at ? <MailOpen className="w-4 h-4 text-white/30" /> : <Mail className="w-4 h-4 text-indigo-400" />}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${m.read_at ? 'text-white/60' : 'text-white font-medium'}`}>{m.comment}</p>
                    <div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/30">
                      <span>من: {m.principal_name}</span>
                      <span>{new Date(m.created_at).toLocaleDateString('ar', { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                    </div>
                  </div>
                </div>
                {!m.read_at ? (
                  <Button size="sm" variant="ghost" onClick={() => markAsRead(m.id)}
                    className="text-emerald-400 hover:text-emerald-300 text-[10px] shrink-0" data-testid={`mark-read-${m.id}`}>
                    <CheckCircle className="w-3.5 h-3.5 ml-1" /> تم القراءة
                  </Button>
                ) : (
                  <span className="text-emerald-400 text-[9px] flex items-center gap-1 shrink-0"><CheckCircle className="w-3 h-3" /> مقروءة</span>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
        {messages.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا توجد رسائل من مدير المدرسة</div>}
      </div>
    </div>
  );
}



// ─── Minister Messages Tab ───
function MinisterMessagesTab({ headers }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchMsgs = useCallback(() => { axios.get(`${API_URL}/api/teacher/minister-messages`, { headers }).then(r => setMessages(r.data)).catch(() => {}).finally(() => setLoading(false)); }, [headers]);
  useEffect(() => { fetchMsgs(); }, [fetchMsgs]);
  const markRead = async (id) => { try { await axios.post(`${API_URL}/api/teacher/minister-messages/${id}/read`, {}, { headers }); setMessages(p => p.map(m => m.id === id ? { ...m, read_at: new Date().toISOString() } : m)); toast.success('تم'); } catch {} };
  if (loading) return <div className="text-white/30 text-center py-12">جارٍ التحميل...</div>;
  const unread = messages.filter(m => !m.read_at).length;
  return (<div className="space-y-4" data-testid="teacher-minister-messages">
    <div className="flex items-center justify-between"><h2 className="text-lg font-bold text-yellow-400 flex items-center gap-2"><Star className="w-5 h-5" /> رسائل الوزير ({messages.length})</h2>{unread > 0 && <span className="text-[10px] px-3 py-1 rounded-full bg-red-500/20 text-red-400 font-bold">{unread} غير مقروءة</span>}</div>
    <div className="space-y-2">{messages.map(m => (
      <Card key={m.id} className={`border-white/10 ${m.read_at ? 'bg-white/[0.03]' : 'bg-yellow-500/5 border-yellow-500/20'}`}><CardContent className="p-4"><div className="flex items-start justify-between gap-3"><div className="flex items-start gap-3 flex-1"><div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${m.read_at ? 'bg-white/5' : 'bg-yellow-500/20'}`}>{m.read_at ? <MailOpen className="w-4 h-4 text-white/30" /> : <Star className="w-4 h-4 text-yellow-400" />}</div><div className="flex-1"><p className={`text-sm ${m.read_at ? 'text-white/60' : 'text-white font-medium'}`}>{m.comment}</p><div className="flex items-center gap-2 mt-1.5 text-[10px] text-white/30"><span>من: {m.minister_name}</span><span>{new Date(m.created_at).toLocaleDateString('ar', { year: 'numeric', month: 'short', day: 'numeric' })}</span></div></div></div>
        {!m.read_at ? <Button size="sm" variant="ghost" onClick={() => markRead(m.id)} className="text-emerald-400 hover:text-emerald-300 text-[10px] shrink-0"><CheckCircle className="w-3.5 h-3.5 ml-1" /> تم القراءة</Button> : <span className="text-emerald-400 text-[9px] flex items-center gap-1 shrink-0"><CheckCircle className="w-3 h-3" /> مقروءة</span>}
      </div></CardContent></Card>
    ))}</div>
    {messages.length === 0 && <div className="text-white/30 text-center py-8 bg-white/5 rounded-xl">لا توجد رسائل من الوزير</div>}
  </div>);
}


// ─── Videos Tab ───
function VideosTab({ headers, subjects }) {
  const [videos, setVideos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedLesson, setSelectedLesson] = useState('');

  const fetchVideos = useCallback(() => {
    axios.get(`${API_URL}/api/teacher/videos`, { headers }).then(r => setVideos(r.data)).catch(() => {});
  }, [headers]);
  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const currentSubject = subjects.find(s => s.id === selectedSubject);
  const lessons = currentSubject?.lessons || [];
  const currentLesson = lessons.find(l => l.id === selectedLesson);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedLesson) { toast.error('اختر المادة والدرس أولاً'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams({
        subject_id: selectedSubject,
        subject_name: currentSubject?.name_ar || '',
        lesson_title: currentLesson?.title_ar || '',
      });
      await axios.post(`${API_URL}/api/teacher/lessons/${selectedLesson}/upload-video?${params}`, formData, {
        headers, timeout: 300000
      });
      toast.success('تم رفع الفيديو');
      fetchVideos();
      e.target.value = '';
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل الرفع'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('حذف الفيديو؟')) return;
    await axios.delete(`${API_URL}/api/teacher/videos/${id}`, { headers });
    fetchVideos();
  };

  return (
    <div className="space-y-4" data-testid="teacher-videos">
      <h2 className="text-lg font-bold text-emerald-400 flex items-center gap-2"><MonitorPlay className="w-5 h-5" /> فيديوهاتي</h2>

      <Card className="bg-white/5 border-white/10">
        <CardContent className="p-4 space-y-3">
          <p className="text-white/50 text-xs">اختر المادة والدرس ثم ارفع الفيديو. يمكنك رفع أكثر من فيديو لنفس الدرس.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <select value={selectedSubject} onChange={e => { setSelectedSubject(e.target.value); setSelectedLesson(''); }}
              className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" data-testid="video-subject">
              <option value="">اختر المادة</option>
              {subjects.map(s => <option key={s.id} value={s.id}>{s.name_ar} - {s.grade_name}</option>)}
            </select>
            <select value={selectedLesson} onChange={e => setSelectedLesson(e.target.value)}
              className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none" disabled={!selectedSubject} data-testid="video-lesson">
              <option value="">اختر الدرس</option>
              {lessons.map(l => {
                const count = videos.filter(v => v.lesson_id === l.id).length;
                return <option key={l.id} value={l.id}>{l.title_ar}{count > 0 ? ` (${count} فيديو)` : ''}</option>;
              })}
            </select>
            <label className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg border text-sm cursor-pointer transition-colors ${
              uploading || !selectedLesson ? 'bg-white/5 border-white/10 text-white/30 cursor-not-allowed' : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
            }`}>
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> جارٍ الرفع...</> : <><Video className="w-4 h-4" /> رفع فيديو</>}
              <input type="file" accept="video/*" className="hidden" onChange={handleUpload} disabled={uploading || !selectedLesson} />
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-2">
        {videos.map((v, idx) => (
          <Card key={v.id} className="bg-white/5 border-white/10">
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <Video className="w-4 h-4 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {v.subject_name && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{v.subject_name}</span>}
                    {v.lesson_title && <span className="text-white/60 text-xs">{v.lesson_title}</span>}
                  </div>
                  <p className="text-white text-sm mt-0.5 truncate">{v.title}</p>
                  <p className="text-white/30 text-[10px]">{new Date(v.created_at).toLocaleDateString('ar')}</p>
                </div>
              </div>
              <button onClick={() => handleDelete(v.id)} className="text-red-400/50 hover:text-red-400 shrink-0 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
            </CardContent>
          </Card>
        ))}
        {videos.length === 0 && <div className="text-white/30 text-center py-8">لم يتم رفع أي فيديو</div>}
      </div>
    </div>
  );
}

const StatMini = ({ icon, value, label, color }) => (
  <div className="text-center bg-white/5 rounded-xl p-3 border border-white/10">
    <div className={`flex justify-center mb-1 ${color}`}>{icon}</div>
    <p className={`text-xl font-bold ${color}`}>{value}</p>
    <p className="text-white/30 text-[10px]">{label}</p>
  </div>
);
